import { useEffect, useRef } from 'react'
import { Storage, Cache } from 'aws-amplify'
import moment from 'moment'
import useSafeState from 'hooks/useSafeState'
import usePrevious from 'hooks/usePrevious'

Cache.configure({
	defaultPriority: 4,
	capacityInBytes: 5000000,
	defaultTTL: 3600000
})

const useImgLink = ({ imgKey, isUploading, imgUrlRef, src: propsSrc }) => {
	const getImgLink = () => {
		if(propsSrc) {
			return propsSrc
		}
		return imgKey ? Cache.getItem(imgKey) : null
	}
	const [isLoading, setIsLoading] = useSafeState(false)
	const [src, setSrc] = useSafeState(getImgLink())
	const previousImgKey = usePrevious(imgKey)
	const hasError = useRef(false)

	if(previousImgKey !== imgKey) {
		hasError.current = false
	}

	const preloadImage = url => new Promise((resolve, reject) => {
		const image = new Image()
		image.onerror = reject
		image.onload = resolve
		image.src = url
	})

	useEffect(() => {
		const expires = 3600
		if(!imgKey && !propsSrc) {
			return setSrc(null)
		}
		if(isLoading || hasError.current) {
			return
		}

		const isExpired = urlString => {
			if(!urlString) {
				return true
			}
			const now = moment().valueOf()
			const expirationTime = getExpiration(urlString)
			if(!expirationTime) {
				return false
			}
			return now > expirationTime
		}
		const getExpiration = urlString => {
			const url = new URL(urlString)
			const issueDate = url.searchParams.get('X-Amz-Date')
			const expiresFromHeader = url.searchParams.get('X-Amz-Expires')
			return moment(issueDate, 'YYYYMMDDTHHmmssZ').valueOf() + parseInt(expiresFromHeader, 10) * 1000
		}


		const loadImg = async () => {
			const linkFromCache = getImgLink()
			let isCached = linkFromCache && !isExpired(linkFromCache)
			if((imgKey || propsSrc) && !isUploading) {
				if(isCached) {
					try {
						await preloadImage(linkFromCache)
						setSrc(linkFromCache)
					} catch(err) {
						Cache.removeItem(imgKey)
						isCached = false
					}
				}

				if(!isCached) {
					setIsLoading(true)
					try {
						const imgSrc = await Storage.get(imgKey, { expires })
						await preloadImage(imgSrc)

						Cache.setItem(imgKey, imgSrc, { expires: moment().valueOf() + expires * 1000 })
						setSrc(imgSrc)
					} catch(e) {
						hasError.current = true
						setSrc(null)
					}
					setIsLoading(false)
				}
			}
		}

		loadImg()
	}, [imgKey, isUploading, setIsLoading, setSrc, src, isLoading, hasError, propsSrc])

	const imgSrc = hasError.current ? null : src
	if(imgUrlRef) {
		imgUrlRef.current = imgSrc
	}

	return { src: imgSrc, isLoading }
}

export default useImgLink
